import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { readStoredRithmicCreds } from '../lib/rithmicCreds';
import { blockRithmicAutoResume } from '../lib/rithmicRelay';
import { getRelayOwnership } from '@core/realtime/relayOwnership';

/**
 * CREDENTIAL-FREE connect (mirrors CQG — broker creds are forbidden from transiting our BE). The POST
 * carries ONLY the chosen `system` name (non-secret) so the BE dials the gateway that HOSTS it —
 * "Rithmic Test" → the Test gateway, every real system (Paper Trading, prop firms, live) → the
 * production gateway; dialing the wrong one is rejected rp_code 1067. The system is read from the SAME
 * localStorage creds the relay uses to build the RequestLogin, so gateway + login system_name always
 * agree. Store creds via storeRithmicCreds BEFORE calling this. Broker username/password never leave
 * the browser — the relay builds + sends the RequestLogin itself at socket-open.
 */
export function useRithmicConnect() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      // Both fields are public identifiers: `system` picks the gateway that HOSTS the login and
      // `location` (2026-07-29) picks which production point-of-presence to dial. Read from the same
      // localStorage creds the relay uses, so the dialed socket and the RequestLogin always agree.
      const { system, location } = readStoredRithmicCreds();
      return api.post<void>('/api/v1/rithmic/connect', { system, location: location || null });
    },
    // Invalidate ['me'] too so any first-connect action flag the BE sets on a successful logon refreshes.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rithmic', 'status'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useRithmicDisconnect() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      // The user's own Disconnect is terminal for auto-resume on every tab and across reloads (D4,
      // 2026-09-13) — latched BEFORE the POST so the state push it triggers cannot race a resume.
      // Only an explicit Connect (runRithmicConnectSequence) releases it. Both Disconnect surfaces
      // (the dialog and the Accounts panel's Unlink) come through this mutation.
      blockRithmicAutoResume('user-disconnected');
      getRelayOwnership('rithmic').release(); // D1: the other tabs learn the owner let go
      return api.post<void>('/api/v1/rithmic/disconnect');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rithmic', 'status'] }),
  });
}

export function useRithmicImport() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from?: string; to?: string; account?: string }) =>
      api.post<{ jobId: string }>('/api/v1/rithmic/import', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trades'] });
      qc.invalidateQueries({ queryKey: ['statistics'] });
    },
  });
}
